import { useRef } from 'react';

import { Dialog as MuiDialog } from '@mui/material';
import { useTheme } from '@mui/material/styles';
// eslint-disable-next-line @nx/enforce-module-boundaries -- Module Federation singleton 키를 유지하려면 패키지 경로 import가 필요합니다.
import { PopupContextProvider } from '@kc-console/common-ui/singleton';

import { useThrottledSubmit } from '../../utils/useThrottledSubmit';
import { Blanket } from '../Blanket';
import { ModalBody } from '../ModalBody';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';

import type { ModalProps } from './types';

const Modal = (props: ModalProps) => {
  const {
    loading = false,
    size = 'sm',
    fullScreen = false,
    slots,
    slotProps,
    onClose,
    children,
    onFormSubmit,
    enableAutoFocus = false,
    ...others
  } = props;

  const dialogRef = useRef<HTMLDivElement>(null);
  const throttledFormSubmit = useThrottledSubmit(onFormSubmit);
  const theme = useTheme();
  const modalPopupZIndex = theme.zIndex.modalPopup;

  const {
    header: Header = ModalHeader,
    body: Body = ModalBody,
    footer: Footer = ModalFooter,
  } = slots ?? {};
  const {
    header: headerProps,
    body: bodyProps,
    footer: footerProps,
    transition: transitionProps,
  } = slotProps ?? {};

  const mergedTransitionProps = enableAutoFocus
    ? {
        ...transitionProps,
        onEntered: (...args: unknown[]) => {
          if (transitionProps?.onEntered) {
            (transitionProps.onEntered as (...args: unknown[]) => void)(...args);
          }

          const autofocusElement =
            dialogRef.current?.querySelector<HTMLElement>('[data-autofocus]');

          if (autofocusElement) {
            autofocusElement.focus();
          } else if (process.env.NODE_ENV === 'development') {
            console.warn(
              '[Modal] enableAutoFocus is true but no element with data-autofocus attribute found. ' +
                'Make sure to use autoFocus prop on TextInputLiner, TextInputGhost, or other supported input components.',
            );
          }
        },
      }
    : transitionProps;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && e.nativeEvent.isComposing) {
      e.preventDefault();
    }
  };

  // loading 중에는 form submit을 차단한다.
  // footer의 제출 버튼은 ModalFooterButton의 disabled로 이미 막히지만 Enter 제출 경로에는 잠금이 없어
  // 두 경로의 동작이 어긋난다. 여기서 막아 onFormSubmit 소비처 전체가 같은 기준으로 잠긴다.
  //
  // loading만으로는 부족하다. loading은 렌더 파생값이라 첫 제출이 loading을 켜고 그 리렌더가
  // 커밋되기까지 간극이 있고, 그 사이의 두 번째 제출은 아직 false인 loading을 통과한다.
  // 그 간극에서의 중복만 throttle로 막는다. 더 긴 비동기 구간은 소비처가 loading에 반영해야 한다.
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // throttle에 걸렸는지 알 수 없으므로 항상 막는다.
    // 걸린 채로 기본 동작이 남으면 form이 네이티브 제출되어 페이지가 새로고침된다.
    e.preventDefault();

    if (loading) {
      return;
    }

    throttledFormSubmit(e);
  };

  const bodyContent = (
    <>
      {bodyProps && Body && <Body loading={loading} {...bodyProps} />}
      {children}
    </>
  );

  return (
    <MuiDialog
      {...others}
      ref={dialogRef}
      onClose={onClose}
      fullScreen={fullScreen}
      slots={{ backdrop: Blanket }}
      slotProps={{ transition: mergedTransitionProps }}
      fullWidth={!fullScreen}
      maxWidth={fullScreen ? false : size}
      scroll={fullScreen ? 'paper' : 'body'}
    >
      {/* Modal 내부 popup/dropdown/date picker는 modal(1400) 위인 modalPopup(1410) 계층을 공유한다. */}
      <PopupContextProvider zIndex={modalPopupZIndex}>
        {headerProps && Header && <Header loading={loading} {...headerProps} onClose={onClose} />}

        {onFormSubmit ? (
          <form onSubmit={handleFormSubmit} onKeyDown={handleKeyDown}>
            {bodyContent}
            <button type="submit" hidden aria-hidden="true" />
          </form>
        ) : (
          bodyContent
        )}

        {footerProps && Footer && <Footer loading={loading} {...footerProps} />}
      </PopupContextProvider>
    </MuiDialog>
  );
};

export default Modal;
